import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL, AppConstant } from '../../../../AppConstant'
import { APIStatus } from '../../../types'

// Types
interface Studio {
  id: string
  name: string
  address?: string
  phone?: string
  email?: string
}

interface Appointment {
  appointmentTime: string
  needsFullSlot: boolean
  count: string
  isMember: boolean
  isScanBooking?: boolean
  wasNonMember?: boolean
  was_non_member?: boolean
  convertedToMember?: boolean
  converted_to_member?: boolean
  memberConvertedAt?: string
  member_converted_at?: string
  /** Per-slot non-member visits; used for header counts only */
  nonMemberCount?: string | number
  /** Per-slot first-visit non-members who later became members; used for header counts only */
  convertedCount?: string | number
  converted_count?: string | number
}

interface MemberCount {
  count: number
}

interface AvailabilityWindow {
  startTime: string
  endTime: string
}

export interface DayAvailability {
  date: string
  isHoliday: boolean
  timezone: string
  windows: Array<AvailabilityWindow>
}

interface StudioState {
  studio: Studio | null
  status: APIStatus
  schedule: Array<Appointment>
  scheduleStatus: APIStatus
  scheduleAvailability: Array<DayAvailability>
  scheduleAvailabilityStatus: APIStatus
  ghostSlotActive: boolean
  ghostSlotTime: string | null
  ghostSlotFetchStatus: APIStatus
  ghostSlotToggleStatus: APIStatus
  memberCount: MemberCount | null
  memberCountStatus: APIStatus
  memberCountError: string | null
  allMemberCount: MemberCount | null
  allMemberCountStatus: APIStatus
  allMemberCountError: string | null
  lastMemberCountPollTime: number | null
  error: string | null
}

// Initial State
const initialState: StudioState = {
  studio: null,
  status: APIStatus.idle,
  schedule: [],
  scheduleStatus: APIStatus.idle,
  scheduleAvailability: [],
  scheduleAvailabilityStatus: APIStatus.idle,
  ghostSlotActive: false,
  ghostSlotTime: null,
  ghostSlotFetchStatus: APIStatus.idle,
  ghostSlotToggleStatus: APIStatus.idle,
  memberCount: null,
  memberCountStatus: APIStatus.idle,
  memberCountError: null,
  allMemberCount: null,
  allMemberCountStatus: APIStatus.idle,
  allMemberCountError: null,
  lastMemberCountPollTime: null,
  error: null,
}

// Async Thunks

export const getScheduleByWeek = createAsyncThunk(
  'studio/getScheduleByWeek',
  async ({ startDate, endDate, clinicId }: { startDate: any; endDate: any; clinicId: any }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/clinic/schedule?startDate=${startDate}&endDate=${endDate}&clinicId=${clinicId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const getScheduleAvailability = createAsyncThunk(
  'studio/getScheduleAvailability',
  async ({ startDate, endDate, clinicId }: { startDate: any; endDate: any; clinicId: any }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/clinic/schedule/availability?startDate=${startDate}&endDate=${endDate}&clinicId=${clinicId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response?.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data)
    } else {
      return thunkAPI.rejectWithValue(response?.data?.message || 'unable_to_fetch_schedule_availability')
    }
  },
)

export const getMemberCount = createAsyncThunk(
  'studio/getMemberCount',
  async ({ clinicId }: { clinicId?: string } = {}, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    const url = clinicId ? `${API_URL}/clinic/${clinicId}/active-members` : `${API_URL}/clinic/active-members`

    let response = await axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data)
    } else {
      return thunkAPI.rejectWithValue(response.data?.message || 'Failed to fetch member count')
    }
  },
)

export const getGhostSlotStatus = createAsyncThunk(
  'studio/getGhostSlotStatus',
  async ({ clinicId }: { clinicId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    const response = await axios
      .get(`${API_URL}/clinic/ghost-slot`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
        params: { clinicId },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response?.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data)
    }
    return thunkAPI.rejectWithValue(response?.data?.message || 'unable_to_fetch_ghost_slot')
  },
)

export const activateGhostSlot = createAsyncThunk(
  'studio/activateGhostSlot',
  async ({ clinicId }: { clinicId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    const response = await axios
      .post(
        `${API_URL}/clinic/ghost-slot`,
        { clinicId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response?.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data)
    }
    return thunkAPI.rejectWithValue(response?.data?.message || 'unable_to_activate_ghost_slot')
  },
)

export const deactivateGhostSlot = createAsyncThunk(
  'studio/deactivateGhostSlot',
  async ({ clinicId }: { clinicId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    const response = await axios
      .delete(`${API_URL}/clinic/ghost-slot`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
        data: { clinicId },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response?.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data)
    }
    return thunkAPI.rejectWithValue(response?.data?.message || 'unable_to_deactivate_ghost_slot')
  },
)

// Slice
const studioSlice = createSlice({
  name: 'studio',
  initialState,
  reducers: {
    setStudio: (state, action: PayloadAction<Studio>) => {
      state.studio = action.payload
    },
    clearStudio: (state) => {
      state.studio = null
      state.error = null
    },
    resetStatus: (state) => {
      state.status = APIStatus.idle
      state.error = null
    },
    resetSchedule: (state) => {
      state.schedule = []
      state.scheduleAvailability = []
      state.scheduleAvailabilityStatus = APIStatus.idle
    },
    resetGhostSlotState: (state) => {
      state.ghostSlotActive = false
      state.ghostSlotTime = null
      state.ghostSlotFetchStatus = APIStatus.idle
      state.ghostSlotToggleStatus = APIStatus.idle
    },
    setLastMemberCountPollTime: (state, action: PayloadAction<number | null>) => {
      state.lastMemberCountPollTime = action.payload
    },
  },
  extraReducers(builder) {
    //getScheduleByWeek
    builder.addCase(getScheduleByWeek.fulfilled, (state, action) => {
      state.schedule = action.payload.data
      state.scheduleStatus = APIStatus.fulfilled
    })
    builder.addCase(getScheduleByWeek.pending, (state, action) => {
      state.scheduleStatus = APIStatus.pending
    })
    builder.addCase(getScheduleByWeek.rejected, (state, action) => {
      state.scheduleStatus = APIStatus.rejected
    })

    //getScheduleAvailability
    builder.addCase(getScheduleAvailability.fulfilled, (state, action) => {
      state.scheduleAvailability = action.payload.data || []
      state.scheduleAvailabilityStatus = APIStatus.fulfilled
    })
    builder.addCase(getScheduleAvailability.pending, (state) => {
      state.scheduleAvailabilityStatus = APIStatus.pending
    })
    builder.addCase(getScheduleAvailability.rejected, (state) => {
      state.scheduleAvailability = []
      state.scheduleAvailabilityStatus = APIStatus.rejected
    })

    // getGhostSlotStatus
    builder.addCase(getGhostSlotStatus.fulfilled, (state, action) => {
      state.ghostSlotFetchStatus = APIStatus.fulfilled
      state.ghostSlotActive = !!action.payload?.data
      state.ghostSlotTime = action.payload?.data?.slotTime || null
    })
    builder.addCase(getGhostSlotStatus.pending, (state) => {
      state.ghostSlotFetchStatus = APIStatus.pending
    })
    builder.addCase(getGhostSlotStatus.rejected, (state) => {
      state.ghostSlotFetchStatus = APIStatus.rejected
      state.ghostSlotActive = false
      state.ghostSlotTime = null
    })

    // activateGhostSlot
    builder.addCase(activateGhostSlot.fulfilled, (state, action) => {
      state.ghostSlotToggleStatus = APIStatus.fulfilled
      state.ghostSlotActive = true
      state.ghostSlotTime = action.payload?.data?.slotTime || null
    })
    builder.addCase(activateGhostSlot.pending, (state) => {
      state.ghostSlotToggleStatus = APIStatus.pending
    })
    builder.addCase(activateGhostSlot.rejected, (state) => {
      state.ghostSlotToggleStatus = APIStatus.rejected
    })

    // deactivateGhostSlot
    builder.addCase(deactivateGhostSlot.fulfilled, (state) => {
      state.ghostSlotToggleStatus = APIStatus.fulfilled
      state.ghostSlotActive = false
      state.ghostSlotTime = null
    })
    builder.addCase(deactivateGhostSlot.pending, (state) => {
      state.ghostSlotToggleStatus = APIStatus.pending
    })
    builder.addCase(deactivateGhostSlot.rejected, (state) => {
      state.ghostSlotToggleStatus = APIStatus.rejected
    })

    //getMemberCount
    builder.addCase(getMemberCount.fulfilled, (state, action) => {
      if (action.meta.arg.clinicId) {
        state.memberCount = action.payload.data
        state.memberCountStatus = APIStatus.fulfilled
        state.memberCountError = null
      } else {
        state.allMemberCount = action.payload.data
        state.allMemberCountStatus = APIStatus.fulfilled
        state.allMemberCountError = null
      }
    })
    builder.addCase(getMemberCount.pending, (state, action) => {
      if (action.meta.arg.clinicId) {
        state.memberCountStatus = APIStatus.pending
      } else {
        state.allMemberCountStatus = APIStatus.pending
      }
    })
    builder.addCase(getMemberCount.rejected, (state, action) => {
      if (action.meta.arg.clinicId) {
        state.memberCountStatus = APIStatus.rejected
        state.memberCountError = (action.payload as string) || 'Failed to fetch member count'
      } else {
        state.allMemberCountStatus = APIStatus.rejected
        state.allMemberCountError = (action.payload as string) || 'Failed to fetch member count'
      }
    })
  },
})

// Export actions
export const { setStudio, clearStudio, resetStatus, resetSchedule, resetGhostSlotState, setLastMemberCountPollTime } =
  studioSlice.actions

// Export reducer
export default studioSlice.reducer
